import { useState } from 'react';
import { TASK_PRIORITIES, type TaskPriority } from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Textarea } from '../../components/ui/Textarea.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog.js';

/** Creates a task outside generation, via POST /tasks (apps/api/src/routes/tasks.ts) — the same
 * endpoint PRD/TDD-driven generation never touches, so a manual task here can't be clobbered by
 * a later regeneration (which only patches tasks with a populated `trace`). */
export function AddTaskDialog({
  projectId,
  open,
  onClose,
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [acceptanceCriteria, setAcceptanceCriteria] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [estimateHours, setEstimateHours] = useState('');
  const [labels, setLabels] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setTitle('');
    setDescription('');
    setAcceptanceCriteria('');
    setPriority('medium');
    setEstimateHours('');
    setLabels('');
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/tasks`, {
        title: title.trim(),
        description,
        acceptanceCriteria: acceptanceCriteria
          .split('\n')
          .map((c) => c.trim())
          .filter(Boolean),
        priority,
        estimateHours: estimateHours.trim() ? Number(estimateHours) : null,
        labels: labels
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
      });
      reset();
      onClose();
    } catch {
      setError('Could not create the task. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Phones lean on the Dialog's own dvh-capped scroll; an inner 80vh one would ignore the
          on-screen keyboard and nest a second scroller. */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 md:max-h-[80vh] md:overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Add task</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new-task-title">Title</Label>
          <Input
            id="new-task-title"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new-task-description">Description</Label>
          <Textarea
            id="new-task-description"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new-task-acceptance">Acceptance criteria</Label>
          <Textarea
            id="new-task-acceptance"
            rows={3}
            placeholder="One per line"
            value={acceptanceCriteria}
            onChange={(e) => setAcceptanceCriteria(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="new-task-priority">Priority</Label>
            <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
              <SelectTrigger id="new-task-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TASK_PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="new-task-estimate">Estimate (hrs)</Label>
            <Input
              id="new-task-estimate"
              type="number"
              min={0}
              value={estimateHours}
              onChange={(e) => setEstimateHours(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new-task-labels">Labels</Label>
          <Input
            id="new-task-labels"
            placeholder="Comma-separated"
            value={labels}
            onChange={(e) => setLabels(e.target.value)}
          />
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="default" disabled={submitting || !title.trim()}>
            {submitting ? 'Adding…' : 'Add task'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
